import {
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { SelectedCheckCircleIcon } from "components/icons";

interface SelectableChipProps {
  label: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  containerStyle?: SxProps<Theme>;
  isSelected?: boolean;
  onClick?: () => void;
  isDisabled?: boolean;
  isShowTickWhenSelected?: boolean;
  fontStyle?: SxProps<Theme>;
  selectedIconColor?: string;
  shape?: "square" | "rounded";
  /**
   * Edge length of the icon slots. Defaults to the 18px the chip was built
   * around; dense rows pass a smaller value so the chip can hold a fixed
   * height without the icon setting the floor.
   */
  iconSize?: string;
  /**
   * Accessible name. Defaults to `label`, which is usually enough — set this
   * when the visible text alone doesn't identify the control (e.g. several
   * chips reading "Tickets" in different rows).
   */
  ariaLabel?: string;
}
/** Resting (unselected) chip shadow, per the design system. */
const REST_SHADOW = "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A";

/**
 * Label/icon colour by state. Module-level so it stays out of the component.
 *
 * Returns `string | undefined` because the extended palette tokens are all
 * declared optional in `src/theme.d.ts` (see the `// FIXME` there). They exist
 * at runtime; MUI treats `undefined` as "property omitted".
 */
const getLabelColor = (
  theme: Theme,
  isDisabled?: boolean,
  isSelected?: boolean,
): string | undefined => {
  if (isDisabled) return theme.palette.lightBg.low;
  // if (isSelected) return theme.palette.purple.main;
  return theme.palette.lightBg.high;
};

export const SelectableChip = ({
  shape = "square",
  label,
  startIcon,
  endIcon,
  containerStyle,
  isSelected,
  isDisabled,
  onClick,
  isShowTickWhenSelected = true,
  fontStyle,
  selectedIconColor,
  ariaLabel,
  iconSize = "18px",
}: SelectableChipProps) => {
  const theme = useTheme();
  const color = getLabelColor(theme, isDisabled, isSelected);
  const isInteractive = Boolean(onClick) && !isDisabled;
  const isRounded = shape === "rounded";
  const borderColor = isSelected
    ? theme.palette.purple.main
    : theme.palette.neutral["015"];

  const activate = () => {
    if (isInteractive) onClick?.();
  };

  return (
    <Stack
      // Exposed as a toggle button: without these the chip is a bare div —
      // unreachable by keyboard and invisible to screen readers, which report
      // neither that it is actionable nor whether it is currently selected.
      role="button"
      tabIndex={isInteractive ? 0 : -1}
      aria-label={ariaLabel ?? label}
      aria-pressed={Boolean(isSelected)}
      aria-disabled={Boolean(isDisabled)}
      onClick={(e) => {
        if (isInteractive) {
          e.stopPropagation();
          activate();
        }
      }}
      onKeyDown={(e) => {
        // Space scrolls the page and Enter can submit a surrounding form, so
        // both are consumed here rather than allowed through.
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          activate();
        }
      }}
      sx={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        borderRadius: isRounded ? "32px" : "4px",
        cursor: isDisabled ? "not-allowed" : "pointer",
        border: `1px solid ${borderColor}`,
        color: color,
        backgroundColor: isSelected
          ? theme.palette.secondary["purple-tint-3"]
          : "transparent",
        // Only the resting state lifts off the page; selected is filled.
        boxShadow: isSelected || isDisabled ? "none" : REST_SHADOW,
        padding: isRounded ? "6px 12px" : "6px 8px",
        // Keyboard users need to see where focus is; `:focus-visible` keeps the
        // ring off mouse clicks. Declared before the spread so a caller can
        // still override it.
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.purple.main}`,
          outlineOffset: "2px",
        },
        ...containerStyle,
      }}
    >
      {startIcon && (
        <Stack sx={{ width: iconSize, height: iconSize }}>{startIcon}</Stack>
      )}
      <Typography
        sx={{
          ...(fontStyle ? fontStyle : theme.typography["body-small"]),
          color: "inherit",
          whiteSpace: "nowrap",
        }}
        color={"inherit"}
      >
        {label}
      </Typography>
      {endIcon && (
        <Stack sx={{ width: iconSize, height: iconSize }}>{endIcon}</Stack>
      )}
      {isShowTickWhenSelected && (
        <Stack sx={{ width: iconSize, height: iconSize }}>
          {isSelected && (
            <SelectedCheckCircleIcon
              htmlColor={selectedIconColor ?? theme.palette.purple.main}
              sx={{ width: iconSize, height: iconSize }}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
